import { useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Product {
  id: string;
  name: string;
  grade: string;
  category: string;
  icon: string;
  description: string;
  applications: string[];
  variants: string[];
}

interface ProductModalProps {
  product: Product;
  onClose: () => void;
}

export default function ProductModal({ product, onClose }: ProductModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-emerald-700 px-6 py-5 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-md shrink-0">
              <i className={`${product.icon} text-white text-xl`} />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">{product.name}</h2>
              <span className="text-emerald-200 text-xs">{product.category}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {/* Grades / Variants */}
          <div className="mb-5">
            <p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">Available Grades / Variants</p>
            <div className="flex flex-wrap gap-2">
              {product.variants.map((v) => (
                <span
                  key={v}
                  className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full border border-emerald-100"
                >
                  {v}
                </span>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="mb-5">
            <p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">Description</p>
            <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
          </div>

          {/* Applications */}
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">Key Applications</p>
            <ul className="flex flex-col gap-2">
              {product.applications.map((app) => (
                <li key={app} className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-checkbox-circle-fill text-emerald-500 shrink-0" />
                  {app}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="flex gap-3">
            <Link
              to="/contact"
              className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold py-2.5 rounded-md text-center whitespace-nowrap cursor-pointer transition-colors"
            >
              Request a Quote
            </Link>
            <button
              onClick={onClose}
              className="flex-1 border border-stone-200 hover:bg-stone-50 text-stone-600 text-sm font-semibold py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
